import type { ReactNode } from "react";
import { Box, HStack, Stack, Text } from "@chakra-ui/react";
import { LuRefreshCw, LuSparkles } from "react-icons/lu";
import { formatRelativeTime } from "@/lib/utils/time";

const animationStyles = `
  @keyframes aiBarShift {
    0% { background-position: 0% 50%; opacity: 0.65; }
    50% { background-position: 100% 50%; opacity: 0.9; }
    100% { background-position: 0% 50%; opacity: 0.65; }
  }
`;

interface AiInfoBoxProps {
  title: string;
  meta?: string;
  description?: string;
  generatedAt?: string | null;
  regenerating?: boolean;
  onRegenerate?: () => void;
  children?: ReactNode;
}

export function AiInfoBox({
  title,
  meta,
  description,
  generatedAt,
  regenerating,
  onRegenerate,
  children,
}: AiInfoBoxProps) {
  return (
    <Box
      position="relative"
      bg="linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(139, 92, 246, 0.02) 100%)"
      border="0.5px solid var(--color-border)"
      borderRadius="8px"
      p={4}
      overflow="hidden"
    >
      <style>{animationStyles}</style>
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="2px"
        bg="linear-gradient(90deg, rgba(99, 102, 241, 0.6), rgba(139, 92, 246, 0.6), rgba(99, 102, 241, 0.2))"
        backgroundSize="200% 100%"
        style={{ animation: "aiBarShift 6s ease-in-out infinite" }}
      />
      <Stack gap={3}>
        <HStack gap={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="24px"
            h="24px"
            borderRadius="6px"
            bg="linear-gradient(135deg, rgba(99, 102, 241, 0.18), rgba(139, 92, 246, 0.18))"
            border="0.5px solid rgba(99, 102, 241, 0.3)"
            color="rgba(99, 102, 241, 0.95)"
          >
            <LuSparkles size={14} />
          </Box>
          <Stack gap={0.5}>
            <Text
              fontSize="11px"
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="0.08em"
              color="rgba(99, 102, 241, 0.9)"
            >
              AI Generated
            </Text>
            <Text fontSize="13px" fontWeight="600" color="var(--color-text-primary)">
              {title}
            </Text>
          </Stack>
        </HStack>

        {meta && (
          <Text fontSize="11px" color="var(--color-text-tertiary)">
            {meta}
          </Text>
        )}

        {(generatedAt || onRegenerate) && (
          <HStack gap={2} flexWrap="wrap">
            {generatedAt && (
              <Text fontSize="11px" color="var(--color-text-tertiary)">
                Generated {formatRelativeTime(generatedAt, "past")}
              </Text>
            )}
            {onRegenerate && (
              <Box
                as="button"
                display="inline-flex"
                alignItems="center"
                gap={1}
                fontSize="11px"
                fontWeight="500"
                color="rgba(99, 102, 241, 0.8)"
                cursor={regenerating ? "default" : "pointer"}
                opacity={regenerating ? 0.5 : 1}
                _hover={regenerating ? {} : { color: "rgba(99, 102, 241, 1)" }}
                onClick={regenerating ? undefined : onRegenerate}
              >
                <LuRefreshCw size={11} style={regenerating ? { animation: "spin 1s linear infinite" } : undefined} />
                {regenerating ? "Regenerating…" : "Regenerate"}
              </Box>
            )}
          </HStack>
        )}

        {description && (
          <Text fontSize="13px" color="var(--color-text-secondary)" lineHeight="1.6">
            {description}
          </Text>
        )}

        {children}
      </Stack>
    </Box>
  );
}
